/* eslint-disable indent */
import React, { FC, useMemo } from "react";
import { StyledDivider } from "components/StyledUI";
import { styled } from "@mui/material";
import type { Filter, FilterEntry } from "types/Search";
import type { CustomerProductlist } from "types/CustomerProductlist";
import { useLocation } from "react-router-dom";
import { useAppSelector } from "_redux/hooks";
import { selectCustomerProductListsWithContractsSorted } from "_redux/selectors/user";
import { useTranslation } from "react-i18next";
import FilterSelectedOptionRow from "./FilterSelectedOptionRow";
import FilterDropdownSimple from "./FilterDropdownSimple";
import FilterDropdowsWithSearch from "./FilterDropdowsWithSearch";
import FilterDropdownProductLists from "./FilterDropdownProductlists";

// Every attribute value that is in this set will be picked up and collected,
// to be displayed in the greenWorld filter
const GREENWORLD_ATTRIBUTES = new Set([
  "asc",
  "bio",
  // Activate in XOSBEC-5438
  // "fairtrade",
  "msc",
  "regionallysourced",
]);

const GREENWORLD_FILTER_VALUE = "greenworldattributes";

interface FilterSectionProps {
  filterList: Filter[];
  selectedProductlistFilter: CustomerProductlist[];
  isAccountListView: boolean;
}

const FilterWrapper = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  flexWrap: "wrap",
  gap: theme.spacing(1),
}));

const FilterSection: FC<FilterSectionProps> = ({
  filterList,
  selectedProductlistFilter,
  isAccountListView,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const customerProductLists = useAppSelector(
    selectCustomerProductListsWithContractsSorted
  );

  // Retrieve allowed labels from translations
  const allowedLabels = t("productFilter.greenWorld.allowedLabelsAndValues", {
    returnObjects: true,
  });

  const GREENWORLD_ALLOWED_LABELS = useMemo(
    () => new Set(Array.isArray(allowedLabels) ? allowedLabels : []),
    [allowedLabels]
  );

  // Add the filters that should be displayed in the greenWorld filter
  const sortedFilterList = useMemo(() => {
    // Extract the filters and their entries that belong to the greenWorldAttributes
    const greenWorldFilters = filterList.filter((f) =>
      GREENWORLD_ATTRIBUTES.has(f.value.toLowerCase())
    );

    const greenWorldEntries: FilterEntry[] = greenWorldFilters.flatMap((f) =>
      /**
       * For the display of the GreenWorld filter, the actual logic with value and label no longer fits.
       * Since the "combined" filter needs additional values to display everything as desired.
       * The optional properties `originLabel` and `originValue` are added to the filter entries to accomplish this.
       */
      f.entry
        .filter(
          (entry) =>
            // Prevent negative values for GreenWorld
            entry?.label &&
            GREENWORLD_ALLOWED_LABELS.has(entry.label.toLowerCase())
        )
        .map((entry) => ({
          label: f.name, // Take the name of the filter as the label
          value: entry.value,
          count: entry.count,
          originLabel: entry.label, // Save the original label
          originValue: f.value, // Save the original filter value
        }))
    );

    // Here the original filters are removed, which are contained in the greenWorld filter group
    const result = [
      ...filterList.filter(
        (f) => !GREENWORLD_ATTRIBUTES.has(f.value.toLowerCase())
      ),
    ];

    // Add the greenWorld filter group if there are entries
    if (greenWorldEntries.length > 0) {
      result.push({
        name: t("productFilter.greenWorld.attributesLabel"),
        value: GREENWORLD_FILTER_VALUE,
        entry: greenWorldEntries,
        extendedData: {
          allowMultipleValueSelection: "true",
          displayable: "true",
          attrname: GREENWORLD_FILTER_VALUE,
        },
      });
    }

    result.forEach((f) =>
      f.entry.sort((a, b) => a.label.localeCompare(b.label))
    );

    return result;
  }, [filterList, t, GREENWORLD_ALLOWED_LABELS]);

  const hashParams = useMemo(
    () =>
      new URLSearchParams(
        location.hash ? location.hash.substring(1) : undefined
      ),
    [location.hash]
  );

  const matchedFilter = useMemo(
    () =>
      sortedFilterList.filter((item) =>
        item.value === GREENWORLD_FILTER_VALUE
          ? item.entry.some(
              (entry) =>
                // For greenWorld, match on entry's originValue if it exists in hashParams
                entry.originValue && hashParams.has(entry.originValue)
            )
          : hashParams.has(item.value)
      ),
    [hashParams, sortedFilterList]
  );

  return (
    <>
      <StyledDivider light />

      <FilterWrapper>
        {!isAccountListView && customerProductLists.length > 0 ? (
          <FilterDropdownProductLists
            selectedProductlistFilter={selectedProductlistFilter}
            customerProductLists={customerProductLists}
          />
        ) : undefined}

        {sortedFilterList.length > 0 &&
          sortedFilterList.map((filter) =>
            filter.entry.length >= 11 ? (
              <FilterDropdowsWithSearch key={filter.value} filter={filter} />
            ) : (
              <FilterDropdownSimple key={filter.value} filter={filter} />
            )
          )}
      </FilterWrapper>
      {(matchedFilter.length > 0 || selectedProductlistFilter?.length > 0) && (
        <>
          <StyledDivider light />
          <FilterSelectedOptionRow
            filterList={filterList}
            selectedProductlistFilter={selectedProductlistFilter}
          />
        </>
      )}
    </>
  );
};

export default FilterSection;
